import React, { useCallback, useState, useEffect } from "react";
import { useLocalStorage } from "hooks/useLocalStorage";
import { post } from "lib/http";
import { HomeRepairCredentials } from "types/HomeRepairCredentials";
import jwt_decode from "jwt-decode";
import axios, { AxiosInstance } from "axios";
import { useRouter } from "next/router";

const CUSTOMER_FILE_AUTH_PATH = "/murfy-erp/customer-file/token/";
const REFRESH_TOKEN = "/token/refresh/";

export type HomeRepairContext = {
  login: (value: HomeRepairCredentials) => Promise<string | null>;
  getCustomerUuid: () => string | null;
  logOut: () => void;
  loading: boolean;
  authenticated: boolean;
  authenticatedAxios: AxiosInstance;
};

export const HomeRepairAuthContext = React.createContext<HomeRepairContext>({} as HomeRepairContext);

type ProviderProps = {
  children: React.ReactNode;
};
export const HomeRepairAuthProvider = ({ children }: ProviderProps) => {
  const router = useRouter();
  const token: string = router.query?.token as string;
  const [refreshToken, setRefreshToken] = useLocalStorage<string | null>("refreshToken", null);
  const [accessToken, setAccessToken] = useState<string | null>();

  const login = async (value: HomeRepairCredentials) => {
    const { data, status } = await post(CUSTOMER_FILE_AUTH_PATH, value);
    if (status !== 200) {
      throw new Error("Invalid credentials");
    }
    setAccessToken(data.access);
    setRefreshToken(data.refresh);
    return getCustomerUuid();
  };

  const logOut = useCallback(() => {
    setRefreshToken(null);
    setAccessToken(null);
  }, [setAccessToken, setRefreshToken]);

  const refreshAccessToken = useCallback(async () => {
    try {
      const response = await post(REFRESH_TOKEN, {
        refresh: refreshToken || token,
      });
      const { data, status } = response;
      if (status === 200) {
        setAccessToken(data.access);
        if (token) {
          setRefreshToken(token);
          router.push(router.pathname);
        }
        return data.access;
      } else {
        logOut();
      }
    } catch {
      logOut();
    }
  }, [logOut, refreshToken, token, setRefreshToken, router]);

  useEffect(() => {
    if (accessToken === undefined) {
      if (refreshToken !== undefined) {
        refreshAccessToken();
      } else {
        logOut();
      }
    }
  }, [accessToken, refreshToken, logOut, refreshAccessToken]);

  const instance = axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  instance.interceptors.request.use(
    (config) => {
      if (accessToken && config.headers && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (err) => {
      const originalConfig = err.config;
      if (originalConfig.url !== CUSTOMER_FILE_AUTH_PATH && !originalConfig._retry) {
        // set retry to prevent infinite loop
        originalConfig._retry = true;
        // Access Token was expired
        if (err.response.status === 401) {
          try {
            const accessToken = await refreshAccessToken();
            const updatedConfig = {
              ...originalConfig,
              headers: {
                ...originalConfig.headers,
                Authorization: `Bearer ${accessToken}`,
              },
            };

            return instance(updatedConfig);
          } catch (_error) {
            return Promise.reject(_error);
          }
        }
      }
      return Promise.reject(err);
    }
  );

  const getDecodedAccessToken = (): { user_id: string } | null => {
    if (!accessToken) {
      return null;
    }
    return jwt_decode(accessToken);
  };

  const loading = accessToken === undefined;
  const authenticated = !!accessToken;

  const getCustomerUuid = (): string | null => {
    const decodedToken = getDecodedAccessToken();
    return decodedToken ? decodedToken.user_id : null;
  };

  const stateValues = {
    login,
    logOut,
    loading,
    authenticated,
    getCustomerUuid,
    authenticatedAxios: instance,
  };

  return <HomeRepairAuthContext.Provider value={stateValues}>{children}</HomeRepairAuthContext.Provider>;
};

export default HomeRepairAuthProvider;
